/*
  Variant media filter (EG Jewelry customization for Broadcast 8.x)

  Shows only the media that belongs to the selected variant.

  Source of truth: the variant image on each variant plus the order of the
  product media. snippets/product.liquid works out the groups and prints
  them on every slide:

    data-media-group     group index, "0" for shared media
    data-media-leader    present when the slide is a variant image
    data-media-position  position in the product's media list
    data-image-id        the Shopify media id (Broadcast)
    data-media-id        section-scoped id used by product-images and
                         product-thumbs (Broadcast)

  Rule, matching product.liquid:
    - The selected variant's image comes first.
    - Then the non-leader media in the same group, in catalog order.
    - Then the shared media (group 0), in catalog order.
    - Everything else, including other leaders in the same group, is
      taken out of the list. Nothing is destroyed: the slides are kept
      in memory and put back when a variant needs them.
    - A variant with no image assigned shows every media item.

  Liquid renders the first variant's set, so this script only has work
  to do when the variant changes.
*/
(function () {
  'use strict';

  var FILTERED_CLASS = 'media--filtered';
  var SLIDE_SELECTOR = '[data-slide][data-media-group]';

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function byPosition(a, b) {
    return (parseInt(a.getAttribute('data-media-position'), 10) || 0) - (parseInt(b.getAttribute('data-media-position'), 10) || 0);
  }

  function byDomOrder(a, b) {
    if (a === b) return 0;
    return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  }

  /* The list of slides the selected variant should show, in order. */
  function computeVisible(allSlides, featuredMediaId) {
    var target = null;
    if (featuredMediaId) {
      allSlides.some(function (slide) {
        if (slide.getAttribute('data-image-id') === featuredMediaId) {
          target = slide;
          return true;
        }
        return false;
      });
    }

    // Safety net: no variant image, or one we cannot find. Show everything.
    if (!target || !target.hasAttribute('data-media-leader')) {
      return { target: null, visible: allSlides.slice() };
    }

    var group = target.getAttribute('data-media-group');
    var visible = [target];

    allSlides.forEach(function (slide) {
      if (slide === target) return;
      if (slide.getAttribute('data-media-group') === group && !slide.hasAttribute('data-media-leader')) {
        visible.push(slide);
      }
    });

    allSlides.forEach(function (slide) {
      if (slide.getAttribute('data-media-group') === '0') visible.push(slide);
    });

    return { target: target, visible: visible };
  }

  /*
    Put `visible` into `container` in that order and take everything else
    in `all` out. Only nodes that are out of place are moved, so slides
    that already sit where they belong are left alone.
  */
  function applyOrder(container, all, visible, anchorSelector, onDetach) {
    var keep = new Set(visible);
    var changed = false;

    all.forEach(function (el) {
      if (!keep.has(el) && el.parentNode === container) {
        container.removeChild(el);
        if (onDetach) onDetach(el);
        changed = true;
      }
    });

    var anchor = anchorSelector ? container.querySelector(anchorSelector) : null;
    var cursor = container.firstElementChild;

    visible.forEach(function (el) {
      el.classList.remove(FILTERED_CLASS);
      if (cursor === el) {
        cursor = el.nextElementSibling;
        return;
      }
      container.insertBefore(el, cursor && cursor !== anchor ? cursor : anchor);
      changed = true;
    });

    return changed;
  }

  function selectedFeaturedMediaId(productInfo, variant) {
    if (variant && variant.featured_media && variant.featured_media.id) {
      return String(variant.featured_media.id);
    }
    var json = productInfo.querySelector('variant-selects [data-selected-variant]');
    if (!json) return null;
    try {
      var parsed = JSON.parse(json.textContent);
      return parsed && parsed.featured_media && parsed.featured_media.id ? String(parsed.featured_media.id) : null;
    } catch (e) {
      return null;
    }
  }

  /*
    Broadcast's product-info.updateMedia() only fires when the variant's
    slide is in the DOM, which it is not once this script has hidden it.
    This mirrors what updateMedia does: mark the slide active (which also
    updates the thumbnails) and scroll to it.
  */
  function activateSlide(state, target) {
    if (!target) return;

    var productImages = state.productImages;
    var mediaId = target.getAttribute('data-media-id');
    if (!productImages || !mediaId) return;
    if (productImages.getAttribute('data-active-media') === mediaId && target.classList.contains('media--active')) return;

    productImages.dispatchEvent(new CustomEvent('theme:media:select', { detail: { id: mediaId } }));

    requestAnimationFrame(function () {
      var isMobile = window.theme && window.theme.isMobile;
      if (!isMobile && !productImages.hasAttribute('data-fader-desktop') && state.variantImageScroll && window.theme && typeof window.theme.scrollTo === 'function') {
        document.dispatchEvent(new CustomEvent('theme:tooltip:close', { bubbles: false, detail: { hideTransition: false } }));
        window.theme.scrollTo(target.getBoundingClientRect().top);
      }
      if (isMobile && !productImages.hasAttribute('data-fader-mobile') && typeof state.mediaList.scrollTo === 'function') {
        state.mediaList.scrollTo({ left: target.offsetLeft });
      }
    });
  }

  /* A slide that leaves the list must not come back looking active. */
  function resetSlide(slide) {
    slide.classList.remove('media--active', 'media--hiding');
    slide.classList.add('media--hidden');
  }

  function resetThumb(thumb) {
    thumb.classList.remove('is-active');
  }

  function applyVisible(state, visible) {
    applyOrder(state.mediaList, state.slides, visible, '.slider__arrows', resetSlide);

    if (state.thumbsHolder) {
      var thumbsInOrder = [];
      visible.forEach(function (slide) {
        var thumb = state.thumbsByMediaId[slide.getAttribute('data-media-id')];
        if (thumb) thumbsInOrder.push(thumb);
      });
      applyOrder(state.thumbsHolder, state.thumbs, thumbsInOrder, null, resetThumb);
    }
  }

  function filter(state, variant) {
    var featuredId = selectedFeaturedMediaId(state.productInfo, variant);
    var result = computeVisible(state.slides, featuredId);

    applyVisible(state, result.visible);
    activateSlide(state, result.target);
  }

  /*
    Keep the zoom lightbox to the visible images. zoom-images caches its
    image list at construction, so createZoom is wrapped to hand it the
    current visible set, with the PhotoSwipe thumbs template rebuilt to
    match. Any problem falls back to Broadcast's own behaviour.
  */
  function patchZoom(state) {
    var zoom = state.productInfo.querySelector('zoom-images');
    if (!zoom || zoom.__variantMediaPatched || typeof zoom.createZoom !== 'function') return;

    var original = zoom.createZoom;
    var allImages = toArray(zoom.images);
    var template = state.productInfo.querySelector('[data-pswp-thumbs-template]');
    var thumbMarkup = {};

    if (template && template.content) {
      toArray(template.content.querySelectorAll('[data-pswp-media]')).forEach(function (figure) {
        thumbMarkup[figure.getAttribute('data-pswp-media')] = figure.outerHTML;
      });
    }

    // Without the per-image markers the thumbs strip cannot be rebuilt, so
    // leave the lightbox alone rather than show mismatched thumbnails.
    if (!allImages.length || !Object.keys(thumbMarkup).length) return;

    zoom.__variantMediaPatched = true;
    zoom.createZoom = function (index) {
      try {
        var clicked = allImages[index];
        var visible = allImages.filter(function (img) {
          var slide = img.closest('[data-slide]');
          return slide && slide.isConnected && !slide.classList.contains(FILTERED_CLASS);
        });
        visible.sort(byDomOrder);

        if (!visible.length) {
          this.images = allImages;
          return original.call(this, index);
        }

        var start = Math.max(0, visible.indexOf(clicked));
        this.images = visible;
        template.innerHTML = visible.map(function (img) {
          var slide = img.closest('[data-slide]');
          return thumbMarkup[slide.getAttribute('data-image-id')] || '';
        }).join('');

        return original.call(this, start);
      } catch (e) {
        this.images = allImages;
        return original.call(this, index);
      }
    };
  }

  /*
    In the thumbnails layout Broadcast's "previous" arrow wraps to the last
    slide it saw at page load, which may be one that is hidden now. Wrap
    to the last visible slide instead.
  */
  function patchArrows(state) {
    var productImages = state.productImages;
    if (!productImages || productImages.__variantMediaPatched || typeof productImages.getPreviousMediaId !== 'function') return;

    productImages.__variantMediaPatched = true;
    productImages.getPreviousMediaId = function () {
      var current = this.querySelector('[data-image-id].media--active');
      var previous = current ? current.previousElementSibling : null;
      if (!previous || !previous.hasAttribute('data-image-id')) {
        var slides = state.mediaList.querySelectorAll('[data-image-id]');
        previous = slides[slides.length - 1];
      }
      return previous ? previous.getAttribute('data-media-id') : null;
    };
  }

  function init(productInfo) {
    if (productInfo.__variantMediaFilter) return;

    var mediaList = productInfo.querySelector('[data-product-media-list]');
    if (!mediaList) return;

    var slides = toArray(mediaList.querySelectorAll(SLIDE_SELECTOR)).sort(byPosition);
    if (slides.length < 2) return;

    var thumbsHolder = productInfo.querySelector('[data-thumbs-slider]');
    var thumbs = thumbsHolder ? toArray(thumbsHolder.querySelectorAll('[data-thumb-item]')) : [];
    var thumbsByMediaId = {};
    thumbs.forEach(function (thumb) {
      var link = thumb.querySelector('[data-thumb-link]');
      if (link) thumbsByMediaId[link.getAttribute('data-media-id')] = thumb;
    });

    var state = {
      productInfo: productInfo,
      productImages: productInfo.querySelector('product-images'),
      mediaList: mediaList,
      slides: slides,
      thumbsHolder: thumbsHolder,
      thumbs: thumbs,
      thumbsByMediaId: thumbsByMediaId,
      variantImageScroll: productInfo.getAttribute('data-variant-image-scroll') === 'true'
    };
    productInfo.__variantMediaFilter = state;

    // Liquid already rendered the selected variant's set first. Take the
    // hidden slides out of the list so nothing else can land on them.
    var initiallyVisible = slides.filter(function (slide) {
      return !slide.classList.contains(FILTERED_CLASS);
    }).sort(byDomOrder);
    applyVisible(state, initiallyVisible);

    patchZoom(state);
    patchArrows(state);

    if (typeof window.subscribe === 'function' && window.theme && window.theme.PUB_SUB_EVENTS) {
      state.unsubscribe = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, function (event) {
        var data = event && event.data ? event.data : {};
        if (data.sectionId && data.sectionId !== productInfo.dataset.section && data.sectionId !== productInfo.dataset.originalSection) return;
        filter(state, data.variant);
      });
    }

    // Fallback for pickers that update the variant JSON without publishing.
    productInfo.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || !target.closest('variant-selects')) return;
      setTimeout(function () {
        if (!productInfo.isConnected) return;
        filter(state, null);
      }, 150);
    });
  }

  function initAll(root) {
    toArray((root || document).querySelectorAll('product-info')).forEach(init);
  }

  initAll(document);

  // product-info elements that arrive later (section re-renders, swaps).
  document.addEventListener('theme:product-info:loaded', function (event) {
    var el = event.target;
    if (el && el.tagName === 'PRODUCT-INFO') init(el);
  });
})();
